import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db, storage } from '../../firebase/config';
import { collection, getDocs, addDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowDownTray } from 'react-icons/hi2';
import { MdDescription } from 'react-icons/md';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { friendlyTimestamp } from '../../utils/dateFormat';


const MaterialsPage = () => {
  const { currentUser, userData } = useAuth();
  const { canUpload, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const [materials, setMaterials] = useState([]);
  const [activeFilter, setActiveFilter] = useState('전체');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);

  // Upload modal
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadMaterials();
  }, [currentUser?.uid, isChildView]);

  const loadMaterials = async () => {
    try {
      if (isChildView && currentUser?.uid) {
        // 학생/학부모: 수업 자료만 로드
        const lookupUid = isParent ? (userData?.children?.[0] || currentUser.uid) : currentUser.uid;
        const classSnap = await getDocs(
          query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
        );
        const myClassIds = classSnap.docs.map(d => d.id);
        if (myClassIds.length === 0) {
          setMaterials([]);
          setLoading(false);
          return;
        }
        const snap = await getDocs(collection(db, 'materials'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMaterials(all.filter(m => myClassIds.includes(m.classId)));
      } else {
        const snap = await getDocs(collection(db, 'materials'));
        setMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error('자료 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  // 실제 데이터에서 카테고리 추출 (수업자료 우선)
  const PRIORITY_ORDER = ['수업자료', '강의자료', '시험자료', '교재', '참고자료'];
  const rawCategories = Array.from(new Set(materials.map(m => m.category).filter(Boolean)));
  const sorted = rawCategories.sort((a, b) => {
    const ai = PRIORITY_ORDER.indexOf(a);
    const bi = PRIORITY_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
  const categories = ['전체', ...sorted];

  const filtered = materials.filter(item => {
    if (activeFilter !== '전체' && item.category !== activeFilter) return false;
    if (searchText) {
      const text = searchText.toLowerCase();
      return (item.title || '').toLowerCase().includes(text) ||
        (item.tags || []).some(t => t.toLowerCase().includes(text));
    }
    return true;
  });

  const handleUpload = async () => {
    if (!formData.title || !formData.file) return;
    setUploading(true);
    try {
      const file = formData.file;
      const storageRef = ref(storage, `materials/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'materials'), {
        title: formData.title,
        category: formData.category || '참고자료',
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        fileUrl,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        uploadedBy: currentUser?.uid || '',
        uploaderName: userData?.name || '',
        createdAt: serverTimestamp(),
      });
      setShowModal(false);
      setFormData({});
      loadMaterials();
    } catch (err) {
      console.error('업로드 실패:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (item) => {
    if (!item.fileUrl) return;
    // WebView 환경: RN에 open:: 메시지로 시스템 브라우저에서 열기
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(`open::${item.fileUrl}`);
    } else {
      // 일반 브라우저: 새 탭에서 열기
      window.open(item.fileUrl, '_blank');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const formatDate = (ts) => friendlyTimestamp(ts);

  const EXT_COLORS = {
    pdf: '#EF4444', doc: '#2563EB', docx: '#2563EB',
    xls: '#22C55E', xlsx: '#22C55E', csv: '#22C55E',
    ppt: '#F97316', pptx: '#F97316',
    hwp: '#2563EB', hwpx: '#2563EB',
    jpg: '#A855F7', jpeg: '#A855F7', png: '#A855F7', gif: '#A855F7', webp: '#A855F7', svg: '#A855F7',
    mp4: '#3B82F6', avi: '#3B82F6', mov: '#3B82F6', mkv: '#3B82F6', webm: '#3B82F6',
    mp3: '#EC4899', wav: '#EC4899', aac: '#EC4899',
    zip: '#78716C', rar: '#78716C', '7z': '#78716C',
    txt: '#6B7280',
  };

  const getFileExt = (item) => {
    const name = (item.fileName || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    return ext;
  };

  return (
    <Container>
      <PageHeader
        title={isChildView ? '수업자료실' : '자료실'}
        rightAction={canUpload ? <AddBtn onClick={() => setShowModal(true)}>+ 자료 업로드</AddBtn> : null}
      />

      <SearchArea>
        <SearchIcon><IoSearchOutline size={20} /></SearchIcon>
        <SearchInput
          placeholder="자료 검색..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </SearchArea>

      <FilterRow>
        {categories.map(cat => (
          <FilterBtn key={cat} $active={activeFilter === cat} $student={isStudent} $parent={isParent} onClick={() => setActiveFilter(cat)}>
            {cat}
          </FilterBtn>
        ))}
      </FilterRow>

      <Content>
        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <EmptyText>{isChildView ? '수업 자료가 없습니다.' : '자료가 없습니다.'}</EmptyText>
        ) : (
          filtered.map(item => (
            <MaterialCard key={item.id} onClick={() => handleDownload(item)}>
              <MaterialRow>
                <FileIconWrap>
                  {getFileExt(item) ? (
                    <FileIconBody>
                      <FileIconFold />
                      <FileIconExt $color={EXT_COLORS[getFileExt(item)] || '#9CA3AF'}>
                        {getFileExt(item).toUpperCase()}
                      </FileIconExt>
                    </FileIconBody>
                  ) : (
                    <MdDescription size={48} color="#FB8C00" />
                  )}
                </FileIconWrap>
                <MaterialInfo>
                  {item.category && <CategoryLabel>[{item.category}]</CategoryLabel>}
                  <MaterialTitle>{item.title}</MaterialTitle>
                  {item.fileName && <MaterialFileName>{item.fileName}</MaterialFileName>}
                  <MaterialMeta>
                    {item.uploaderName && `${item.uploaderName} · `}
                    {formatDate(item.createdAt)}
                    {item.fileSize ? ` · ${formatSize(item.fileSize)}` : ''}
                  </MaterialMeta>
                </MaterialInfo>
                <DownloadIcon>
                  <HiOutlineArrowDownTray size={20} />
                </DownloadIcon>
              </MaterialRow>
            </MaterialCard>
          ))
        )}
      </Content>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>자료 업로드</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>제목 *</FieldLabel>
              <FieldInput
                placeholder=""
                value={formData.title || ''}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              />
              <FieldLabel>카테고리</FieldLabel>
              <FieldSelect
                value={formData.category || '참고자료'}
                onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
              >
                <option value="강의자료">강의자료</option>
                <option value="시험자료">시험자료</option>
                <option value="참고자료">참고자료</option>
              </FieldSelect>
              <FieldLabel>태그 (쉼표 구분)</FieldLabel>
              <FieldInput
                placeholder=""
                value={formData.tags || ''}
                onChange={e => setFormData(p => ({ ...p, tags: e.target.value }))}
              />
              <FieldLabel>파일 *</FieldLabel>
              <FieldInput
                type="file"
                onChange={e => setFormData(p => ({ ...p, file: e.target.files[0] }))}
              />
              <ModalButtons>
                <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleUpload} disabled={uploading}>
                  {uploading ? '업로드 중...' : '업로드'}
                </SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MaterialsPage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const AddBtn = styled.button`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid rgba(255,255,255,0.5);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const SearchArea = styled.div`
  position: relative;
  padding: 12px 16px 0;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 28px;
  top: 24px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FilterRow = styled.div`
  display: flex;
  padding: 4px 16px 0;
  gap: 24px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
  &::-webkit-scrollbar { display: none; }
`;

const FilterBtn = styled.button`
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $active }) => $active ? 700 : 400};
  border: none;
  border-bottom: 2px solid ${({ $active, $student, $parent, theme }) => $active ? ($parent ? theme.colors.parentPrimary : $student ? theme.colors.studentPrimary : theme.colors.primary) : 'transparent'};
  color: ${({ $active, $student, $parent, theme }) => $active ? ($parent ? theme.colors.parentPrimary : $student ? theme.colors.studentPrimary : theme.colors.primary) : theme.colors.text.hint};
  background: none;
  white-space: nowrap;
  flex-shrink: 0;
  margin-bottom: -1px;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const MaterialCard = styled.button`
  width: 100%;
  text-align: left;
  padding: 16px;
  margin-bottom: 12px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadow.small};
  &:active { opacity: 0.7; }
`;

const MaterialRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
`;

const FileIconWrap = styled.div`
  flex-shrink: 0;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s;
  &:hover, &:active { opacity: 0.7; }
`;

const FileIconBody = styled.div`
  width: 42px;
  height: 52px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 4px 10px 4px 4px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
`;

const FileIconFold = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #E5E7EB 50%, #D1D5DB 50%);
  border-radius: 0 0 0 3px;
`;

const FileIconExt = styled.span`
  font-size: 10px;
  font-weight: 800;
  color: ${({ $color }) => $color};
  letter-spacing: -0.3px;
`;

const CategoryLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 2px;
`;

const MaterialInfo = styled.div`
  flex: 1;
`;

const MaterialTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-bottom: 4px;
`;

const MaterialFileName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MaterialMeta = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
`;


const DownloadIcon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 4px;
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  &:disabled { opacity: 0.6; }
`;
